import React, { useRef, useCallback, useEffect, useMemo, useState, useContext, forwardRef } from 'react';
import { withTranslation } from 'react-i18next';

import { UserStorageContext } from 'context/UserStorageContext';

import RichMDEditor from 'components/RichMDEditor';

const InputTextEditor = forwardRef(({ id, enterToSubmit, allowEmptySubmit, placeholder, onUpPress, onSubmit, emptyOnSubmit, style, autofocus, value, users, simpleMd, provideController, onKeyDown, onChange, onFocus, onBlur, onFilesPaste, t }: any, ref) => {
    const editorCtrl = useRef(null);
    const userStorage = useContext(UserStorageContext);

    const isPopulated = useCallback(() => {
        return !editorCtrl.current?.isEmpty();
    }, [editorCtrl]);

    const storeValue = useCallback(value => {
        if (id) {
            userStorage.saveEditorValue(id, value);
        }
    }, [id]);

    const submit = useCallback(() => {
        onSubmit && onSubmit(editorCtrl.current?.getValue());

        if (emptyOnSubmit) {
            editorCtrl.current?.reset();
        }

        storeValue('');
    }, [onSubmit, editorCtrl, storeValue, emptyOnSubmit]);

    const handleReturn = useCallback(event => {
        event.preventDefault();

        if (!enterToSubmit || event.shiftKey) {
            return false;
        }

        if (!isPopulated() && !allowEmptySubmit) {
            return true;
        }

        submit();

        return true;
    }, [enterToSubmit, allowEmptySubmit, isPopulated, submit]);

    const handleUpPress = useCallback(event => {
        if (isPopulated()) {
            return;
        }

        onUpPress && onUpPress(event);
    }, [isPopulated, onUpPress]);

    const computedPlaceholder = useMemo(() => {
        return placeholder ?? t('placeholder.message');
    }, [placeholder, t]);

    useEffect(() => {
        provideController && provideController({
            insertEmoji: colons => {
                editorCtrl.current?.insertEmoji(colons);
            },
            focus: () => {
                editorCtrl.current?.focus();
            },
            blur: () => {
                editorCtrl.current?.blur();
            },
            reset: () => {
                editorCtrl.current?.reset();
            },
            isPopulated: () => isPopulated(),
            getValue: () => editorCtrl.current?.getValue()
        });

        // const storeInterval = setInterval(() => {
        //     storeValue(editorCtrl.current?.getValue(true));
        // }, 10000);

        return () => {
            storeValue(editorCtrl.current?.getValue(true));

            // clearInterval(storeInterval);

            provideController && provideController(null);
        }
    }, []);

    return (
        <div
            className="input-text-editor nice-scroll"
            style={style}
        >
            <RichMDEditor
                ref={ref}
                placeholder={computedPlaceholder}
                autofocus={autofocus}
                blurOnEnter={false}
                value={value || userStorage.getEditorValue(id)}
                mentionUsers={users}
                simpleMd={simpleMd}
                onKeyDown={onKeyDown}
                onChange={onChange}
                onFocus={onFocus}
                onBlur={onBlur}
                onReturn={handleReturn}
                onUpPress={handleUpPress}
                onFilesPaste={onFilesPaste}
                provideController={editorCtrl}
            />
        </div>
    );
});

InputTextEditor.defaultProps = {
    users: [],
    enterToSubmit: true,
    allowEmptySubmit: false
};

export default withTranslation('chat')(InputTextEditor);
